import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Button,
  Tooltip,
  AppBar,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { useSelector } from 'react-redux';
import OrderConfirmModal from './OrderConfirmModal/OrderConfirmModal';

const TabBar = ({
  view,
  handleViewChange,
  customerNumber,
  webOrderId,
  handleOrderPlaced,
  onOpenHelp,
  onPersistRemoveItem,
  onPersistQuantityChange,
}) => {
  const theme = useTheme();
  const cartItems = useSelector((state) => state.smartOrderCart.value);

  const totalItems = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + (Number(item.suggested_qty) || 0), 0);
  }, [cartItems]);

  const totalCost = useMemo(() => {
    return cartItems.reduce(
      (sum, item) => sum + (Number(item.cost) || 0) * (Number(item.suggested_qty) || 0),
      0
    );
  }, [cartItems]);

  const formattedTotalCost = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(totalCost);
  }, [totalCost]);

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  const openOrderModal = useCallback(() => {
    if (totalItems > 0) {
      setIsOrderModalOpen(true);
    } else {
      console.warn('Checkout button clicked with no items in cart.');
    }
  }, [totalItems]);

  const closeOrderModal = useCallback(() => {
    setIsOrderModalOpen(false);
  }, []);

  const onOrderPlaced = useCallback(() => {
    closeOrderModal();
    handleOrderPlaced();
  }, [handleOrderPlaced, closeOrderModal]);

  const renderTabLabel = useCallback(
    (title, subtitle) => (
      <Box display='flex' flexDirection='column' alignItems='center' lineHeight={1.2}>
        <Typography
          variant='h6'
          fontWeight={theme.typography.fontWeightMedium}
          sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {subtitle}
        </Typography>
      </Box>
    ),
    [theme.typography.fontWeightMedium]
  );

  return (
    <>
      <AppBar
        position='sticky'
        color='default'
        elevation={1}
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {' '}
          <Box>
            <Tooltip title='Help & FAQs' placement='bottom-start'>
              <Button
                color='inherit'
                onClick={() => onOpenHelp?.()}
                startIcon={<HelpOutlineIcon />}
                aria-label='Help and FAQs'
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Help
              </Button>
            </Tooltip>
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Tabs
              value={view}
              onChange={(e, value) => handleViewChange(value)}
              variant='scrollable'
              scrollButtons
              allowScrollButtonsMobile
              aria-label='Main navigation'
              sx={{
                '& .MuiTab-root': {
                  minWidth: 120,
                  fontWeight: theme.typography.fontWeightMedium,
                  fontSize: '1.5rem',
                  textTransform: 'none',
                },
              }}
            >
              <Tab label={renderTabLabel('Smart Order', 'Priority Replenishment')} value='card' />
              <Tab
                label={renderTabLabel('Smart Buys', 'Suggested Opportunities')}
                value='suggestions'
              />
              <Tab
                label={renderTabLabel('Smart Items', 'Manage Min/Max Settings')}
                value='settings'
              />
            </Tabs>
          </Box>
          <Box sx={{ whiteSpace: 'nowrap' }}>
            {' '}
            <Tooltip
              title={totalItems > 0 ? 'Proceed to Checkout' : 'Add items to cart to checkout'}
            >
              <span>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<ShoppingCartCheckoutIcon />}
                  onClick={openOrderModal}
                  disabled={totalItems === 0}
                  aria-label={`Checkout ${totalItems} items, total cost ${formattedTotalCost}`}
                >
                  {`Checkout (${totalItems}) ${formattedTotalCost}`}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {isOrderModalOpen && (
        <OrderConfirmModal
          handleClose={closeOrderModal}
          customerNumber={customerNumber}
          webOrderId={webOrderId}
          handleOrderPlaced={onOrderPlaced}
          onPersistRemoveItem={onPersistRemoveItem}
          onPersistQuantityChange={onPersistQuantityChange}
        />
      )}

    </>
  );
};

export default TabBar;
